import React, { useState, useEffect } from 'react';
import {collection, query, where, orderBy, getDocs, limit} from 'firebase/firestore';
import { firestore, auth } from '../../firebase';
import { Box, Typography, List, ListItem, ListItemText, Chip, Divider, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ItemModal from '../trade/detail'; // ItemModal 컴포넌트를 import

const StyledListItem = styled(ListItem)(({ theme }) => ({
  transition: '0.3s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  padding: theme.spacing(2),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const MyItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // ItemModal 관련 상태 추가
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchMyItems(currentUser.uid);
      } else {
        setLoading(false);
        setError('로그인이 필요합니다.');
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchMyItems = async (userId) => {
    const q = query(
      collection(firestore, 'items'),
      where('createdBy', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(7)
    );

    try {
      const querySnapshot = await getDocs(q);
      const fetchedItems = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error fetching items:', error);
      setError('아이템을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 아이템 클릭 핸들러 추가
  const handleItemClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  // 모달 닫기 핸들러 추가
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  // 아이템 완료 처리 핸들러 추가
  const handleItemComplete = (id) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, completed: true } : item
    ));
  };

  // 아이템 삭제 핸들러 추가
  const handleItemDelete = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, mt: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>{error}</Typography>
        {!user && (
          <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
            로그인하러 가기
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mt: 2 }}>
      <Typography variant="h4" gutterBottom>My item(7개 표시)</Typography>
      {items.length === 0 ? (
        <Typography>등록된 아이템이 없습니다.</Typography>
      ) : (
        <List>
          {items.map((item) => (
            <React.Fragment key={item.id}>
              <StyledListItem button onClick={() => handleItemClick(item)}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" noWrap>
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="body2">
                        BattlenetID: {item.gameId}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {item.gold} gold
                      </Typography>
                    </Box>
                  }
                />
                <StyledChip
                  label={item.completed ? '판매 완료' : '거래 가능'}
                  color={item.completed ? 'secondary' : 'primary'}
                  size="small"
                />
              </StyledListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      )}
      {selectedItem && (
        <ItemModal
          open={isModalOpen}
          handleClose={handleCloseModal}
          handleComplete={handleItemComplete}
          handleDelete={handleItemDelete}
          item={selectedItem}
        />
      )}
    </Box>
  );
};

export default MyItems;
